import React, { useState, useEffect } from 'react';

const InstallAppButton = ({ variant = 'banner', className = '' }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem('pwa_install_dismissed') === '1'; } catch { return false; }
  });

  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isSamsung = /SamsungBrowser/.test(ua);
  const isInAppBrowser = /FBAN|FBAV|Instagram|Line|Twitter|Snapchat|WhatsApp|WeChat/i.test(ua);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
  const isChrome = /Chrome/.test(ua) && !/Edg|OPR|Brave/.test(ua);

  useEffect(() => {
    if (isStandalone) { setIsInstalled(true); return; }
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => setIsInstalled(true));
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [isStandalone]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') setIsInstalled(true);
      setDeferredPrompt(null);
    } else {
      setShowGuide(true);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem('pwa_install_dismissed', '1'); } catch {}
  };

  if (isInstalled || dismissed) return null;

  const canAutoInstall = !!deferredPrompt;
  const installLabel = canAutoInstall ? 'Install Now' : 'Install';
  const subtitle = canAutoInstall ? 'Tap to install instantly — no app store needed'
    : isInAppBrowser ? 'Open in Chrome or Safari to install'
    : isIOS ? 'Tap below for quick 3-step install'
    : isSamsung ? 'Tap below for quick Samsung install steps'
    : 'Tap to add InfoPilot to your home screen';

  const getGuideContent = () => {
    if (isInAppBrowser) {
      return (
        <div style={{ color: '#d4d4d8', fontSize: '0.85rem', lineHeight: 1.8 }}>
          <p style={{ color: '#f59e0b', fontWeight: 700, marginBottom: 8 }}>
            <i className="fas fa-exclamation-triangle" style={{ marginRight: 6 }} />
            You're in an in-app browser
          </p>
          <p><strong>1.</strong> Tap the <i className="fas fa-ellipsis-h" style={{ color: '#3b82f6' }} /> menu (three dots)</p>
          <p><strong>2.</strong> Select <strong>"Open in {isIOS ? 'Safari' : 'Chrome'}"</strong></p>
          <p><strong>3.</strong> Then tap <strong>"Install"</strong> again from there</p>
        </div>
      );
    }
    if (isIOS) {
      return (
        <div style={{ color: '#d4d4d8', fontSize: '0.85rem', lineHeight: 1.8 }}>
          <p><strong>Step 1:</strong> Tap the <i className="fas fa-share-square" style={{ color: '#3b82f6', fontSize: '1rem' }} /> <strong>Share</strong> button at the bottom of Safari</p>
          <p><strong>Step 2:</strong> Scroll down and tap <strong style={{ color: '#047857' }}>"Add to Home Screen"</strong></p>
          <p><strong>Step 3:</strong> Tap <strong style={{ color: '#10b981' }}>"Add"</strong> in the top right corner</p>
          <p style={{ color: '#a1a1aa', fontSize: '0.75rem', marginTop: 8 }}>
            <i className="fas fa-info-circle" style={{ marginRight: 4 }} />
            Apple requires these steps — no app store download needed!
          </p>
        </div>
      );
    }
    if (isSamsung) {
      return (
        <div style={{ color: '#d4d4d8', fontSize: '0.85rem', lineHeight: 1.8 }}>
          <p><strong>Step 1:</strong> Tap the <i className="fas fa-bars" style={{ color: '#3b82f6' }} /> <strong>menu</strong> button (three lines)</p>
          <p><strong>Step 2:</strong> Tap <strong style={{ color: '#047857' }}>"Add page to"</strong></p>
          <p><strong>Step 3:</strong> Select <strong style={{ color: '#10b981' }}>"Home screen"</strong></p>
        </div>
      );
    }
    return (
      <div style={{ color: '#d4d4d8', fontSize: '0.85rem', lineHeight: 1.8 }}>
        <p><strong>Step 1:</strong> Tap the <i className="fas fa-ellipsis-v" style={{ color: '#3b82f6' }} /> <strong>menu</strong> button (three dots)</p>
        <p><strong>Step 2:</strong> Tap <strong style={{ color: '#047857' }}>"Install App"</strong> or <strong style={{ color: '#047857' }}>"Add to Home Screen"</strong></p>
        <p><strong>Step 3:</strong> Confirm the installation</p>
      </div>
    );
  };

  if (variant === 'inline') {
    return (
      <button onClick={handleInstall} data-testid="install-app-inline-btn" className={className} style={{
        background: canAutoInstall ? 'linear-gradient(135deg, #10b981, #059669)' : 'linear-gradient(135deg, #10b981, #047857)',
        border: 'none', borderRadius: 10, padding: '8px 16px', color: '#ffffff', cursor: 'pointer',
        fontSize: '0.8rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6,
        transition: 'transform 0.15s, box-shadow 0.15s', boxShadow: '0 2px 10px rgba(16, 185, 129, 0.3)',
        touchAction: 'manipulation', minHeight: 44
      }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.03)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
      >
        <i className={canAutoInstall ? 'fas fa-bolt' : 'fas fa-download'} /> {installLabel}
      </button>
    );
  }

  return (
    <>
      <div data-testid="install-app-banner" style={{
        background: 'linear-gradient(135deg, rgba(186, 230, 253, 0.25), rgba(209, 250, 229, 0.35))',
        border: '1px solid rgba(16, 185, 129, 0.28)', borderRadius: 12, padding: '10px 14px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
        marginBottom: 10
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: canAutoInstall ? 'linear-gradient(135deg, #10b981, #059669)' : 'linear-gradient(135deg, #10b981, #0EA5E9)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
          }}>
            <i className={canAutoInstall ? 'fas fa-bolt' : 'fas fa-rocket'} style={{ color: '#ffffff', fontSize: '0.9rem' }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ color: '#0F172A', fontSize: '0.82rem', fontWeight: 700 }}>
              Get InfoPilot as an App
            </div>
            <div style={{ color: '#475569', fontSize: '0.68rem' }}>
              {subtitle}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button onClick={handleInstall} data-testid="install-app-btn" style={{
            background: canAutoInstall ? 'linear-gradient(135deg, #10b981, #059669)' : 'linear-gradient(135deg, #10b981, #0EA5E9)',
            border: 'none', borderRadius: 8, padding: '7px 14px', color: '#ffffff', cursor: 'pointer',
            fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap', touchAction: 'manipulation', minHeight: 44,
            boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
          }}>
            <i className={canAutoInstall ? 'fas fa-bolt' : 'fas fa-download'} style={{ marginRight: 4 }} />
            {installLabel}
          </button>
          <button onClick={handleDismiss} data-testid="install-dismiss-btn" style={{
            background: 'rgba(15, 23, 42, 0.05)', border: '1px solid rgba(15, 23, 42, 0.12)',
            borderRadius: 8, padding: '7px 10px', color: '#64748B', cursor: 'pointer', fontSize: '0.75rem',
            touchAction: 'manipulation', minHeight: 44
          }}>
            <i className="fas fa-times" />
          </button>
        </div>
      </div>

      {showGuide && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.75)', zIndex: 9999, display: 'flex',
          alignItems: 'center', justifyContent: 'center', padding: 16
        }} onClick={() => setShowGuide(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#ffffff', borderRadius: 16, padding: 24, maxWidth: 380, width: '100%',
            border: '1px solid rgba(16, 185, 129, 0.28)', boxShadow: '0 20px 60px rgba(14, 165, 233, 0.25)'
          }}>
            <h3 style={{ color: '#0F172A', fontSize: '1.1rem', fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="fas fa-mobile-alt" style={{ color: '#10B981' }} /> Install InfoPilot
            </h3>
            {getGuideContent()}
            <button onClick={() => setShowGuide(false)} style={{
              width: '100%', marginTop: 16, background: 'linear-gradient(135deg, #10B981, #059669)', border: 'none',
              borderRadius: 10, padding: '12px 0', color: '#ffffff', cursor: 'pointer',
              fontSize: '0.85rem', fontWeight: 700, touchAction: 'manipulation', minHeight: 44,
              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.35)'
            }}>Got it!</button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallAppButton;
